import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import TextArea from '../../../components/TextArea';
import { getCodeError, getVisitTypeNameError, getVisitTypeDescriptionError } from '../../../utils/validation';

function AddVisitTypeModal({ open, onClose, onConfirm, visitTypeData, visitTypeCategories = [], editMode = false }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    categoryId: null,
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when dialog opens or when visitTypeData changes
  useEffect(() => {
    if (open) {
      if (editMode && visitTypeData) {
        setFormData({
          id: visitTypeData.id,
          code: visitTypeData.code || '',
          name: visitTypeData.name || '',
          description: visitTypeData.description || '',
          categoryId: visitTypeData.categoryId || null,
        });
      } else {
        setFormData({
          code: '',
          name: '',
          description: '',
          categoryId: null,
        });
      }
      setErrors({});
    }
  }, [open, visitTypeData, editMode]);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'code') {
      const codeError = getCodeError(value, t);
      if (codeError) {
        newErrors.code = codeError;
      } else {
        delete newErrors.code;
      }
    } else if (field === 'name') {
      const nameError = getVisitTypeNameError(value, t);
      if (nameError) {
        newErrors.name = nameError;
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      const descriptionError = getVisitTypeDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.code.trim() !== '' && formData.name.trim() !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate code: required
    if (!formData.code.trim()) {
      newErrors.code = t('visitTypes.addDialog.codeRequired');
    } else {
      const codeError = getCodeError(formData.code, t);
      if (codeError) {
        newErrors.code = codeError;
      }
    }

    // Validate name: required with regex pattern
    const nameError = getVisitTypeNameError(formData.name, t);
    if (nameError) {
      newErrors.name = nameError;
    }

    // Validate description: max 255 characters with safe characters only
    const descriptionError = getVisitTypeDescriptionError(formData.description, t);
    if (descriptionError) {
      newErrors.description = descriptionError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: editMode ? 'primary.main' : 'success.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('visitTypes.editDialog.title') : t('visitTypes.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {/* Code */}
          <Input
            label={t('visitTypes.addDialog.code')}
            value={formData.code}
            onChange={(e) => handleChange('code', e.target.value)}
            required
            error={!!errors.code}
            helperText={errors.code}
            placeholder={t('visitTypes.addDialog.code')}
            id="code"
            name="code"
            data-testid="visitType-modal-input-code"
          />

          {/* Name */}
          <Input
            label={t('visitTypes.addDialog.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
            placeholder={t('visitTypes.addDialog.name')}
            id="name"
            name="name"
            data-testid="visitType-modal-input-name"
          />

          {/* Description */}
          <TextArea
            label={t('visitTypes.addDialog.description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            rows={3}
            placeholder={t('visitTypes.addDialog.description')}
            id="description"
            name="description"
            data-testid="visitType-modal-input-description"
          />

          {/* Visit Type Category */}
          <Select
            label={t('visitTypes.addDialog.category')}
            value={formData.categoryId || ''}
            onChange={(e) => handleChange('categoryId', e.target.value || null)}
            options={visitTypeCategories.map(vtc => ({ value: vtc.id, label: vtc.name }))}
            placeholder={t('visitTypes.addDialog.selectCategory')}
            emptyMessage={t('visitTypes.addDialog.noCategories')}
            id="categoryId"
            name="categoryId"
            data-testid="visitType-modal-select-category"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="visitType-modal-button-cancel"
        >
          {t('visitTypes.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="visitType-modal-button-confirm"
        >
          {editMode ? t('visitTypes.editDialog.confirm') : t('visitTypes.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddVisitTypeModal;
